import React from 'react';
import moment from 'moment';

import { TicketsTabTrip } from 'Services/Api/_HumanWritten/BookingWizardDataService';
import { TripClassPrice } from 'Services/Api/_HumanWritten/PricingService/PricingService';
import { formatStandardTime } from 'Util/_HumanWritten/TimeUtils';
import { formatPriceDisplay } from 'Util/_HumanWritten/PriceFormattingUtils';
import { classColour } from 'Modules/Ferries/Utils/seatGrid';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { Button, Colors, Display, Sizes } from 'Views/Components/Button/Button';
import { useLegOptionClick } from 'Modules/MultiStopWizard/Hooks/useLegOptionClick';
import {
	BookingWizardData,
	fareFieldLeg,
	passengerCountsByType,
	passengerFaresByLeg,
	patchAllPassengers,
} from '../../BookingWizardData/BaseBookingWizardData';
import { passengerClassPatch } from '../../BookingWizardData/PassengerDetail';
import { classPartyTotal, splitPartyTotal } from './tripClassFares';
import { formatFareChange } from './fareChange';
import { mixGroups, mixedTravellers } from './mixedFares';
import { capacityLabel, classCapacityTag, fareAvailability } from './fareAvailability';
import { selectedClassId } from './TripFareColumns';
import ClassWizardPoints from '../Seating/ClassWizardPoints';
import { describeClassPoints } from '../Seating/seatingLayout';
import ClassInfoPopover from './ClassInfoPopover';

/**
 * The sailing a booker has settled on, collapsed.
 *
 * <p><b>Not the table row with a border round it.</b> Once the choice is made the three columns are
 * answering a question that has been answered: two of them are classes the booker did not take, and
 * keeping them costs the width that the one they did take now needs — for its name, what it
 * includes, and how much of it is left. So the row becomes a statement of what they have, and the
 * classes they passed over come back as a single upgrade line.</p>
 *
 * <p>The way out is one control, not two: "change trip time or class" reopens the table, where both
 * of those are chosen in the same place they were chosen the first time.</p>
 */
interface Props {
	trip: TicketsTabTrip;
	classPrices?: TripClassPrice[];
	wizardData: BookingWizardData;
	saveChanges: (next: BookingWizardData) => void;
	legType: 'departure' | 'return';
	/**
	 * What this sailing changes the bill by, against the booking as it was.
	 *
	 * <p>Null outside an alteration. Inside one it replaces the fare everywhere on this card: the
	 * trip is paid for, so its full price is a figure the customer is not being asked for, and a card
	 * headed "your departing trip" printing it reads as a second charge.</p>
	 */
	alterationDelta?: number | null;
	/**
	 * The two ends of the journey, named beside the date once the sailing is chosen.
	 *
	 * <p>The line above the card carries them too, but it belongs to the step rather than to the
	 * card — and on a phone the card is what a booker screenshots, forwards, and comes back to.
	 * Absent on a leg whose locations have not loaded, where the date stands alone.</p>
	 */
	from?: string;
	to?: string;
}

export default function SelectedFareTrip({
	trip,
	// Defaulted rather than assumed. The map is keyed by trip id and a sailing the pricing engine
	// returned nothing for is simply absent from it, so every read below was one missing key away
	// from taking the step down with it.
	classPrices = [],
	wizardData,
	saveChanges,
	legType,
	from,
	to,
	alterationDelta,
}: Props) {
	const counts = React.useMemo(() => passengerCountsByType(wizardData), [wizardData]);
	const changeTrip = useLegOptionClick(legType, wizardData, trip, true, saveChanges);
	const chosenId = selectedClassId(wizardData, legType);
	const partySize = Object.values(counts).reduce((total, count) => total + count, 0);

	const chosen = chosenId && chosenId !== 'MIXED'
		? classPrices.find(x => x.passengerClassId === chosenId)
		: undefined;

	/**
	 * What the party costs with everybody in their own fare type.
	 *
	 * <p>The one figure a split booking has, and until now the card carried none: `chosen` is
	 * undefined for a mixed party, so the price disappeared exactly when the booker most needed to
	 * see what the sailing was going to cost them. Null where a fare type on the booking is not sold
	 * on this sailing, which the seating step is where to sort out.</p>
	 */
	const splitTotal = React.useMemo(
		() => (chosenId === 'MIXED' ? splitPartyTotal(classPrices, passengerFaresByLeg(wizardData, legType)) : null),
		[chosenId, classPrices, wizardData, legType],
	);

	/**
	 * Who is in which fare type, on the sailing they have taken.
	 *
	 * <p>The card used to say "Mixed fare types" and stop, which is the one thing the booker already
	 * knew. What they are checking at this point is whether the split is the one they meant — so the
	 * card answers with the split itself, grouped by fare type and naming the travellers in each.</p>
	 */
	const groups = React.useMemo(
		() => (chosenId === 'MIXED' ? mixGroups(mixedTravellers(wizardData, legType), classPrices) : []),
		[chosenId, wizardData, legType, classPrices],
	);
	const chosenIndex = chosen ? classPrices.indexOf(chosen) : -1;
	// The description as points, so the dashes an operator types to list them are not printed.
	const chosenPoints = describeClassPoints(chosen?.description);
	const chosenTotal = chosen ? classPartyTotal(chosen, counts) : 0;

	const tag = chosen ? classCapacityTag(trip.capacityTags, chosen.passengerClassId) : undefined;
	const availability = fareAvailability(tag, partySize);
	// Only when it is running out. A collapsed card saying "256 seats left" is a number nobody
	// needed; "only 2 left" is the one fact that might still change the booker's mind.
	const seatsLeft = availability === 'AVAILABLE' ? null : capacityLabel(tag, availability);

	/**
	 * The cheapest class that costs more than the one they took and can still take this party.
	 *
	 * <p>The next step up rather than the best class on the boat: an upgrade line is an offer, and an
	 * offer the booker has to be talked into over two price jumps is an advertisement.</p>
	 */
	const upgrade = chosen
		? classPrices
			.map(classPrice => ({ classPrice, total: classPartyTotal(classPrice, counts) }))
			.filter(x => x.total > chosenTotal)
			.filter(x => {
				const state = fareAvailability(
					classCapacityTag(trip.capacityTags, x.classPrice.passengerClassId),
					partySize,
				);
				return state !== 'SOLD_OUT' && state !== 'NOT_ENOUGH';
			})
			.sort((a, b) => a.total - b.total)[0]
		: undefined;

	const moveEveryoneTo = (passengerClassId: string) => {
		// The fare type alone. A seat the new fare type does not sell is dropped by the seating step,
		// which is the only place that knows which seats each fare type carries — clearing it here
		// took the seat away even when the new fare type sold it.
		saveChanges(
			patchAllPassengers(wizardData, () => passengerClassPatch(
				fareFieldLeg(wizardData, legType),
				passengerClassId,
				classPrices.find(x => x.passengerClassId === passengerClassId)?.name,
			)),
		);
	};

	return (
		<div className="fare-chosen">
			<div className="fare-chosen__main">
				<div className="fare-chosen__rail" aria-hidden="true" />

				<div className="fare-chosen__trip">
					<span className="fare-chosen__label">
						<Icon name="tick" />
						Your {legType === 'departure' ? 'departing' : 'returning'} trip
					</span>
					<span className="fare-chosen__times">
						<span>{formatStandardTime(trip.departureDateTime)}</span>
						{/* The same pair as the cards this summary replaces, so choosing a sailing
						    does not change what the journey looks like. */}
						<Icon name="ship" classname="fare-chosen__mark" />
						<i aria-hidden="true" />
						<Icon name="location-pin" classname="fare-chosen__mark" />
						<span>{formatStandardTime(trip.arrivalDateTime)}</span>
					</span>
						{/*
							Where, when, where. The two places are drawn only at phone width — a
							desktop has the step's own heading line in view directly above this,
							and saying it twice in two sizes reads as two different facts.
						*/}
						<span className="fare-chosen__route">
							<span className="fare-chosen__place">{from}</span>
							<span className="fare-chosen__date">
								{moment(trip.departureDateTime).format('ddd D MMM')}
							</span>
							<span className="fare-chosen__place">{to}</span>
						</span>
				</div>

				<div className="fare-chosen__class">
					{chosen ? (
						<>
							<div className="fare-chosen__detail">
								<span className="fare-chosen__name">
									<span
										className="fare-card__swatch"
										style={{ background: classColour(chosenIndex, chosen.colour) }}
									/>
									{chosen.name}
									<ClassInfoPopover
										classPrice={chosen}
										counts={counts}
										colour={classColour(chosenIndex, chosen.colour)}
									/>
								</span>
								{chosenPoints.length > 0 && (
									<span className="fare-chosen__desc">{chosenPoints.join(' · ')}</span>
								)}
								<ClassWizardPoints attributes={chosen.attributes} />
								{seatsLeft && (
									<span className="fare-chosen__left">{seatsLeft} in this fare type</span>
								)}
							</div>
							<span className="fare-chosen__price">
								{alterationDelta == null && <em>From</em>}
								{alterationDelta != null
									? formatFareChange(alterationDelta)
									: formatPriceDisplay(chosenTotal, false)}
							</span>
						</>
					) : (
						/*
							Two different situations, and they were being drawn as one. MIXED is a
							party split across classes, which the seating step exists to sort out.
							Null is nobody in any class at all — which is not a split, and telling a
							booker travelling alone that their travellers are in different classes is
							nonsense.
						*/
						chosenId === 'MIXED' ? (
							<div className="fare-chosen__mix">
								<div className="fare-chosen__mix-head">
									<span className="fare-chosen__mix-title">Fare types</span>
									<span className="fare-chosen__mix-count">
										{`${partySize} traveller${partySize === 1 ? '' : 's'}`}
									</span>
								</div>

								<ul className="fare-chosen__mix-list">
									{groups.map(group => {
										const classPrice = classPrices
											.find(x => x.passengerClassId === group.passengerClassId);
										const index = classPrice ? classPrices.indexOf(classPrice) : -1;
										return (
											<li
												key={group.passengerClassId ?? 'none'}
												className="fare-chosen__mix-row"
											>
												<span className="fare-chosen__mix-who">
													{group.summary}
												</span>
												<span className="fare-chosen__mix-fare">
													<span
														className="fare-card__swatch"
														style={{
															background: classColour(index, group.colour),
														}}
													/>
													{group.name}
													{/*
														The same ⓘ as everywhere else: the one thing
														a booker checking their split may still want
														is what the dearer fare type actually buys.
													*/}
													{classPrice && (
														<ClassInfoPopover
															classPrice={classPrice}
															counts={counts}
															colour={classColour(index, group.colour)}
														/>
													)}
												</span>
											</li>
										);
									})}
								</ul>

								{(alterationDelta != null || splitTotal !== null) && (
									<div className="fare-chosen__mix-total">
										<span>
											{alterationDelta != null
												? 'Price change'
												: `Total for ${partySize}`}
										</span>
										<b>
											{alterationDelta != null
												? formatFareChange(alterationDelta)
												: formatPriceDisplay(splitTotal ?? 0, false)}
										</b>
									</div>
								)}
							</div>
						) : (
							<div className="fare-chosen__detail">
								<span className="fare-chosen__name">No fare type chosen</span>
								<span className="fare-chosen__desc">
									Pick a fare type to finish choosing this trip.
								</span>
							</div>
						)
					)}
				</div>
			</div>

			<div className="fare-chosen__footer">
				{upgrade && (
					<span className="fare-chosen__upgrade">
						<span
							className="fare-card__swatch"
							style={{
								background: classColour(
									classPrices.indexOf(upgrade.classPrice),
									upgrade.classPrice.colour,
								),
							}}
						/>
						<span>
							Upgrade to <b>{upgrade.classPrice.name}</b> for{' '}
							<b>+{formatPriceDisplay(upgrade.total - chosenTotal, false)}</b>
							{upgrade.classPrice.description
								? ` — ${describeClassPoints(upgrade.classPrice.description).join(' · ')}`
								: ''}
						</span>
						{/*
							`newStyles` is the app's current button: the rounded radius and the
							medium weight every other button in the wizard carries. Without it this
							one arrived with the old square corners and bold text, which is what made
							it look borrowed from somewhere else.
						*/}
						<Button
							className="fare-chosen__upgrade-action"
							display={Display.Outline}
							colors={Colors.Secondary}
							sizes={Sizes.Small}
							newStyles
							onClick={() => moveEveryoneTo(upgrade.classPrice.passengerClassId)}
						>
							Upgrade
						</Button>
					</span>
				)}

				{/*
					Reopens the table. Same handler as the fare cells' de-select, because it is the
					same act — the sailing goes back to being one of the day's options.
				*/}
				<Button
					className={`fare-chosen__change ${
						legType === 'departure' ? 'departure-ticket' : 'return-ticket'
					} change-selected-ticket`}
					display={Display.Text}
					onClick={changeTrip}
				>
					Change trip time or fare type{chosenId === 'MIXED' ? 's' : ''}
				</Button>
			</div>
		</div>
	);
}
